import { type ReactElement } from 'react'
import { type PermissionDefinition, type PermissionType } from '@terros/common'
import { Group, Stack, Table, Text, Tooltip } from '@mantine/core'
import { type usePermissionComparisons } from '@front/model'
import { getPermissionActionDescription, TButton } from '@components'
import { PermissionScopeCell } from './PermissionScopeCell'
import { PERMISSION_COLUMN_WIDTH } from './permissionComparisonConstants'
import { type CompareEntity, isPermissionValueDifferentFromControl } from './compareUtil'

type PermissionComparisonRowProps = {
  category?: string
  definition: PermissionDefinition
  permissionType: PermissionType
  results: ReturnType<typeof usePermissionComparisons>
  selectedEntities: CompareEntity[]
}

export const PermissionComparisonRow = ({
  category,
  definition,
  permissionType,
  results,
  selectedEntities,
}: PermissionComparisonRowProps): ReactElement => {
  const description = `${getPermissionActionDescription(definition)} for:`

  return (
    <Table.Tr>
      <Table.Td className='border-default-border bg-body sticky left-0 z-[1] border-r' w={PERMISSION_COLUMN_WIDTH}>
        <Stack gap='xs'>
          {category && (
            <Text c='dimmed' size='xs'>
              {category}
            </Text>
          )}
          <Group gap='xs' wrap='nowrap'>
            <Text className='min-w-0 font-semibold'>{definition.label}</Text>
            <Tooltip
              events={{ focus: true, hover: true, touch: true }}
              label={description}
              maw={360}
              multiline
              withArrow
            >
              <TButton
                aria-label={`${definition.label} description`}
                buttonType='icon-rounded'
                className='cursor-help'
                icon='help'
                iconProps={{ color: 'var(--mantine-color-dimmed)', size: 'sm' }}
                size='xs'
                type='button'
                variant='text'
              />
            </Tooltip>
          </Group>
        </Stack>
      </Table.Td>
      {results.map((result, index) => (
        <PermissionScopeCell
          key={selectedEntities[index]?.id ?? permissionType}
          isControl={index === 0}
          isDifferent={isPermissionValueDifferentFromControl(results[0]?.data?.list, result.data?.list, permissionType)}
          permissionType={permissionType}
          result={result}
        />
      ))}
    </Table.Tr>
  )
}
